import { EditorState, Plugin, PluginKey, Selection, Transaction } from 'prosemirror-state'

import Styles from './EmptyPrompt.module.scss'
import EmptyPromptContent from './EmptyPromptContent'
import { isInTable } from 'prosemirror-tables'
import { Decoration, DecorationSet } from 'prosemirror-view'
import { trackChangesKey } from 'common/Prose/Plugins/TrackChanges/constants'
import { isDeletion, isInsertion } from 'common/Prose/Plugins/TrackChanges/utils'
import { schema } from 'prose-core'
import { PredictAIPluginKey, PredictAIPluginState, PredictAIStatus } from '../PredictAI/PredictAI'
import { isEmptyDoc } from 'common/Prose/utils/isEmptyDoc'
import { AIInlinePromptPluginKey } from 'common/Prose/Plugins/AIInlinePrompt/AIInlinePrompt'
import { isMobile } from 'utils/isMobile'
import { createRoot } from 'react-dom/client'

export const emptyPromptKey = new PluginKey('emptyPrompt')

const LISTS_WITH_EXTRA_MARGIN: { [key: string]: number } = {
  unorderedList: 1,
  unorderedListSingle: 1,
  orderedList: 1,
  orderedListSingle: 1,
}

// To hide EmptyPrompt if cursor is where a full paragraph deletion is.
const isCursorOnChange = (state: EditorState<any>, selection: Selection<any>) => {
  const node = selection.$from.node()
  if (node.type !== schema.nodes.paragraph) {
    return false
  }
  const trackChangesState = trackChangesKey.getState(state)
  if (trackChangesState) {
    const nextNode = state.doc
      .resolve(Math.min(selection.$from.pos + selection.$from.node().nodeSize, state.doc.nodeSize - 2))
      .node()
    return (
      isDeletion(nextNode) || isDeletion(node) || (!trackChangesState.store.hideDecorations && isInsertion(nextNode))
    )
  }
}

const containerDom = document.createElement('div')
const root = createRoot(containerDom)

const decorateEmptyPrompt = (
  from: number,
  tr: Transaction,
  inlineCompletion: boolean,
  isEmpty: boolean,
  state: EditorState,
) => {
  const wrapper = document.createElement('div')
  wrapper.appendChild(containerDom)
  containerDom.classList.add(Styles.positionContainer)
  containerDom.classList.add('EmptyPrompt')
  const positionBefore = from - 2 >= 0 ? from - 2 : 0

  const nameOfNodeBefore = tr.doc.resolve(positionBefore).parent.type.name
  // There are some nodes which have weird paddings/margins in the editor. We need to account for them
  // and give the empty prompt the right amount of margin
  if (LISTS_WITH_EXTRA_MARGIN[nameOfNodeBefore]) {
    containerDom.classList.add(`${Styles.afterLists}`)
  } else if (from !== 1 && nameOfNodeBefore === 'doc') {
    containerDom.classList.add(`${Styles.afterLabel}`)
  }

  const aiPromptEnabled = !!AIInlinePromptPluginKey.getState(state)

  root.render(
    <EmptyPromptContent inlineCompletion={inlineCompletion} isEmptyDoc={isEmpty} aiPromptEnabled={aiPromptEnabled} />,
  )

  return DecorationSet.create(tr.doc, [Decoration.widget(from - 1, wrapper)])
}

interface Config {
  inlineCompletion?: boolean
}

const EmptyPrompt = ({ inlineCompletion = false }: Config) => {
  return new Plugin({
    key: emptyPromptKey,
    state: {
      init(_, state: EditorState) {
        return {
          inlineCompletion,
          decorations: DecorationSet.create(state.tr.doc, []),
          visible: false,
        }
      },

      apply(tr, oldPluginState, oldEditorState, newEditorState) {
        const {
          schema,
          selection,
          selection: {
            $anchor: { parent, depth },
          },
        } = newEditorState

        if (
          !isCursorOnChange(newEditorState, selection) &&
          selection.empty &&
          !isInTable(newEditorState) &&
          parent &&
          parent.type === oldEditorState.schema.nodes.paragraph &&
          parent.content.size <= 0 &&
          depth === 1 &&
          !parent.attrs.depth &&
          !tr.getMeta('showSnippetPreview') &&
          !schema.marks.code.isInSet(newEditorState.storedMarks || [])
        ) {
          if (oldEditorState.selection?.eq(selection) && oldPluginState.decorations.local.length && isMobile()) {
            return oldPluginState
          }

          return {
            inlineCompletion,
            decorations: decorateEmptyPrompt(
              selection.$from.pos,
              tr,
              inlineCompletion,
              isEmptyDoc(newEditorState),
              newEditorState,
            ),
            visible: true,
          }
        }
        return {
          inlineCompletion,
          decorations: DecorationSet.create(tr.doc, []),
          visible: false,
        }
      },
    },
    props: {
      decorations(state) {
        const aiPromptState = AIInlinePromptPluginKey.getState(state)
        const isAIPromptActive = aiPromptState && aiPromptState.active
        const status = (PredictAIPluginKey.getState(state) as PredictAIPluginState)?.status
        if (status === PredictAIStatus.predicting || isAIPromptActive) {
          return DecorationSet.empty
        }
        return emptyPromptKey.getState(state).decorations
      },
    },
  })
}

export default EmptyPrompt
